import {
  type ActionKind,
  type HintTone,
  type StepAction,
  type StepHint,
  hintButton,
} from '@tabel/shared/sites/step-texts';
import {
  ArrowUpRight,
  CircleAlert,
  CircleCheck,
  Clock,
  Download,
  Globe,
  Info,
  type LucideIcon,
  RefreshCw,
  RotateCcw,
  ShieldCheck,
  TriangleAlert,
} from 'lucide-react';
import { Button, Callout, LinkButton } from '../../../components/ui';

/*
 * Подсказки шага (007 T041; spec.md US3, FR-013, FR-019, FR-020, FR-022, FR-023; дизайн-система Callout и Steps):
 * итог проверки подсказкой `Callout` — заголовок жирным, текст и действия-ссылки в конце. Главное действие подсказки
 * «нужно исправить» (`hintButton`) — кнопка с иконкой, как «Выдать новый пароль» (на телефоне 40 px): ссылку «Разрешить адрес» в красной подсказке не
 * нашли (21.10.2026). Тексты и действия — только из `@tabel/shared/sites/step-texts` (`stepView`), значения из деталей
 * сервера — только текстом. Что делает действие, решает страница (`onAction`); внешняя ссылка «Программа AmneziaWG»
 * открывается в новой вкладке со стрелкой ↗.
 */

/** Вид подсказки → вид `Callout`: ожидание — как сведения, со значком часов. */
const TONE: Readonly<Record<HintTone, 'accent' | 'ok' | 'warn' | 'bad'>> = {
  info: 'accent',
  wait: 'accent',
  ok: 'ok',
  warn: 'warn',
  bad: 'bad',
};
const ICON: Readonly<Record<HintTone, LucideIcon>> = {
  info: Info,
  wait: Clock,
  ok: CircleCheck,
  warn: TriangleAlert,
  bad: CircleAlert,
};
/** Иконка кнопки главного действия (дизайн-система Button: вторичная — белая с иконкой); и в тревогах площадки. */
export const FIX_ICON: Partial<Record<ActionKind, LucideIcon>> = {
  recheck: RefreshCw,
  allowAddress: ShieldCheck,
  retryPrepare: RotateCcw,
  restartControl: RotateCcw,
  switchToDirect: Globe,
  downloadAmneziaConfig: Download,
};

export function StepHints({
  hints,
  onAction,
  disabled,
}: {
  hints: readonly StepHint[];
  onAction: (a: StepAction) => void;
  /** Закрытый шаг или идёт запрос: действия видны, но не нажимаются. */
  disabled?: boolean;
}) {
  if (hints.length === 0) return null;
  return (
    <div className="flex flex-col gap-2">
      {hints.map((h, i) => {
        const main = hintButton(h);
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: подсказки шага приходят списком без своих ключей, порядок постоянный
          <div key={i} data-testid="step-hint" data-tone={h.tone}>
            <Callout tone={TONE[h.tone]} icon={ICON[h.tone]}>
              <p>
                {h.title && <b className="font-semibold">{h.title}</b>}
                {h.title && h.text ? ' ' : null}
                {h.text}
              </p>
              {h.actions.length > 0 && (
                <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1">
                  {h.actions.map((a) => (
                    <HintAction
                      key={`${a.kind}-${a.label}`}
                      action={a}
                      main={a === main}
                      onAction={onAction}
                      disabled={disabled}
                    />
                  ))}
                </div>
              )}
            </Callout>
          </div>
        );
      })}
    </div>
  );
}

function HintAction({
  action: a,
  main,
  onAction,
  disabled,
}: { action: StepAction; main: boolean; onAction: (a: StepAction) => void; disabled?: boolean }) {
  if (a.kind === 'openAmnezia')
    return (
      <a
        href={a.href}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 font-medium text-accent underline-offset-2 hover:underline max-sm:min-h-10"
      >
        {a.label}
        <ArrowUpRight size={14} strokeWidth={1.75} aria-hidden />
      </a>
    );
  if (main)
    return (
      <Button icon={FIX_ICON[a.kind]} disabled={disabled} onClick={() => onAction(a)}>
        {a.label}
      </Button>
    );
  return (
    <LinkButton disabled={disabled} onClick={() => onAction(a)}>
      {a.label}
    </LinkButton>
  );
}
